<script setup lang="ts">
// McpWorkspacePicker (ORP-21466) — the rail's workspace control: the selected workspace's name
// with the billing account's name beneath it, exactly as the 2026-08-23 design draws it.
//
// It does NOT re-implement switching. `WorkspaceSwitcher` (module 07) already owns the flow, the
// access check, the pending state and the failure copy; this component supplies the account line
// the web topbar gets from `AccountArea` and which the MCP rail has no other home for. Switching
// remounts the routed view through `McpLayout`'s `:key="workspaceId"`, same as the web shell.
//
// The picker renders even with a single workspace (AC 4): one option, still visible, so the user
// never loses the concept — hiding it is what made v3 users think workspaces did not exist.
//
// ⚠ Name clash, not a duplicate: `features/integrations/components/mcp/McpWorkspacePicker.vue` is
// an unrelated per-CONNECTION workspace binder inside the MCP connections table.
import { computed } from 'vue'

import { useAccount, useWorkspace, WorkspaceSwitcher } from '@/features/account'

const { account } = useAccount()
const { workspace } = useWorkspace()

const accountName = computed(() => {
  const name = account.value?.name
  return typeof name === 'string' && name.trim().length > 0 ? name : ''
})
const workspaceName = computed(() => String(workspace.value?.label ?? ''))
/** The design's square avatar: the workspace initial, falling back to the account's. */
const initial = computed(() => (workspaceName.value || accountName.value).slice(0, 1).toUpperCase())
</script>

<template>
  <div class="mcp-workspace-picker" data-testid="mcp-workspace-picker">
    <div class="mcp-workspace-picker__identity">
      <span v-if="initial" class="mcp-workspace-picker__avatar" aria-hidden="true">
        {{ initial }}
      </span>
      <span class="mcp-workspace-picker__names">
        <span class="mcp-workspace-picker__workspace" data-testid="mcp-workspace-picker-workspace">
          {{ workspaceName }}
        </span>
        <span
          v-if="accountName"
          class="mcp-workspace-picker__account"
          data-testid="mcp-workspace-picker-account"
        >
          {{ accountName }}
        </span>
      </span>
    </div>
    <WorkspaceSwitcher class="mcp-workspace-picker__switcher" />
  </div>
</template>

<style scoped>
.mcp-workspace-picker {
  display: grid;
  gap: var(--ui-space-2xs);
  padding: var(--ui-space-xs);
  border: 1px solid var(--ui-color-border);
  border-radius: var(--ui-radius-md);
}
.mcp-workspace-picker__identity {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: var(--ui-space-xs);
}
.mcp-workspace-picker__avatar {
  display: grid;
  width: 1.375rem;
  height: 1.375rem;
  flex: none;
  place-items: center;
  border-radius: var(--ui-radius-sm);
  background: var(--ui-color-surface-sunken);
  color: var(--ui-color-fg);
  font-size: 0.625rem;
  font-weight: 700;
}
.mcp-workspace-picker__names {
  min-width: 0;
  flex: 1;
}
.mcp-workspace-picker__names > span {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mcp-workspace-picker__workspace {
  color: var(--ui-color-fg);
  font-size: 0.75rem;
  font-weight: 600;
}
.mcp-workspace-picker__account {
  color: var(--ui-color-muted);
  font-size: 0.625rem;
}
</style>
